import { useState } from 'react'
import { Link } from 'react-router-dom'
import Avatar from './Avatar.jsx'
import Markdown from './Markdown.jsx'
import ScorePill from './ScorePill.jsx'
import { STATE_LABELS } from '../lib/score.js'

/**
 * One review, wherever it appears — an album page, a profile, a feed card.
 *
 * Three things it has to get right.
 *
 * **A spoiler is covered, not withheld.** The body is already in the payload,
 * so revealing it is a state change rather than a round trip. Withholding it
 * server-side would mean a fetch to read a paragraph you are entitled to.
 *
 * **Hidden and deleted read differently.** "A moderator hid this" and "the
 * author took this down" are different sentences, and a reader deserves to
 * know which happened. Neither has a body to show; both keep their slot so
 * the conversation underneath is not orphaned.
 *
 * **The score beside it is the author's current one.** It comes from a live
 * join on the server, so a re-rate moves it and deleting the rating takes it
 * away, and neither touches the prose.
 */
function Byline({ author, mine }) {
  if (!author) return null
  return (
    <Link to={`/profile/${author.username}`} className="review-byline">
      <Avatar name={author.display_name || author.username} avatarUrl={author.avatar_url} size={32} />
      <span className="row-main">
        <span className="review-author">{author.display_name || author.username}</span>
        <span className="footnote">@{author.username}{mine ? ' · you' : ''}</span>
      </span>
    </Link>
  )
}

function Tombstone({ status }) {
  const byModerator = status === 'hidden'
  // Not `hidden` as the modifier: Tailwind ships `.hidden { display: none }`,
  // so the tombstone rendered and then vanished — on exactly the case it
  // exists for. Found in the browser, because the markup was right.
  return (
    <div className={`review-tombstone ${byModerator ? 'by-moderator' : 'by-author'}`}>
      <p className="mb-0">
        {byModerator ? 'A moderator hid this review.' : 'The author removed this review.'}
      </p>
    </div>
  )
}

export default function ReviewCard({
  review,
  onReact,
  onDelete,
  onEdit,
  onReport,
  showMedia = false,
  children,
}) {
  const [revealed, setRevealed] = useState(false)
  if (!review) return null

  const unreadable = review.status === 'hidden' || review.status === 'deleted'
  const covered = review.spoiler && !revealed && !unreadable

  return (
    <article className="card card-pad review-card fade-up">
      <header className="review-head">
        <Byline author={review.author} mine={review.is_mine} />
        <div className="review-head-right">
          {review.status === 'draft' ? <span className="chip">Draft</span> : null}
          {review.score ? (
            <ScorePill
              score={review.score.score}
              mine={review.is_mine}
              label={`${review.author?.display_name || 'Their'} score`}
            />
          ) : null}
        </div>
      </header>

      {showMedia && review.media ? (
        <Link
          to={`/${review.media.kind === 'song' ? 'song' : 'album'}/${review.media.public_id}`}
          className="review-subject"
        >
          <span className="review-subject-title">{review.media.title}</span>
          <span className="footnote">{review.media.credit_name}</span>
        </Link>
      ) : null}

      {review.score?.state ? (
        <p className="footnote mt-1 mb-0">{STATE_LABELS[review.score.state]}</p>
      ) : null}

      {unreadable ? (
        <Tombstone status={review.status} />
      ) : covered ? (
        <button type="button" className="spoiler-cover" onClick={() => setRevealed(true)}>
          <span className="spoiler-label">Spoilers</span>
          <span className="footnote">Tap to read</span>
        </button>
      ) : (
        <Markdown html={review.body_html} className="review-body" />
      )}

      {review.status === 'hidden' && review.is_mine && review.body_html ? (
        <details className="review-own-hidden">
          <summary className="footnote">Your copy</summary>
          <Markdown html={review.body_html} className="review-body" />
        </details>
      ) : null}

      <footer className="review-foot">
        <button
          type="button"
          className={`icon-btn ${review.reacted ? 'on' : ''}`}
          onClick={() => onReact?.(review)}
          aria-pressed={review.reacted}
          aria-label={review.reacted ? 'Remove your heart' : 'Heart this review'}
          disabled={unreadable}
        >
          <span aria-hidden="true">♥</span> {review.reaction_count || 0}
        </button>
        <span className="footnote">
          {review.comment_count || 0} {review.comment_count === 1 ? 'comment' : 'comments'}
        </span>
        <span className="row-main" />
        {review.is_mine ? (
          <>
            {onEdit ? (
              <button type="button" className="btn btn-quiet btn-sm" onClick={() => onEdit(review)}>
                Edit
              </button>
            ) : null}
            {onDelete ? (
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => onDelete(review)}
              >
                Delete
              </button>
            ) : null}
          </>
        ) : onReport ? (
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => onReport(review)}>
            Report
          </button>
        ) : null}
      </footer>

      {children}
    </article>
  )
}
